import { Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { AlertTriangle, Home, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

type AppErrorBoundaryProps = {
  children: ReactNode;
  pageLevel?: boolean;
};

type AppErrorBoundaryState = {
  hasError: boolean;
  isChunkError: boolean;
};

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.message.includes("Failed to fetch dynamically imported module") ||
    error.message.includes("Importing a module script failed") ||
    error.name === "ChunkLoadError"
  );
}

export default class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = {
    hasError: false,
    isChunkError: false,
  };

  static getDerivedStateFromError(error: unknown) {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error, {
      tags: { context: "app-error-boundary" },
      extra: { componentStack: errorInfo.componentStack },
    });

    // Chunk load errors mean the SW served stale bundles — force an SW update and reload.
    if (isChunkLoadError(error)) {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          registrations.forEach((reg) => reg.update());
        });
      }
    }
  }

  private handleReload = () => {
    this.setState({ hasError: false, isChunkError: false });
    window.location.reload();
  };

  private handleGoHome = () => {
    this.setState({ hasError: false, isChunkError: false });
    window.location.href = "/app/dashboard";
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const isPageBoundary = this.props.pageLevel;
    const isChunk = this.state.isChunkError;

    const errorCard = (
      <div className="w-full max-w-md rounded-2xl border border-border/70 bg-card p-6 shadow-sm">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-expense/10 text-expense">
          <AlertTriangle className="h-6 w-6" />
        </div>
        <h1 className="text-xl font-semibold tracking-tight">
          {isChunk ? "Versão desatualizada" : "Não conseguimos abrir esta tela"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {isChunk
            ? "Uma nova versão do sistema foi publicada. Recarregue para atualizar."
            : "O sistema encontrou um erro inesperado. Você pode recarregar a página ou ir para o Dashboard."}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button onClick={this.handleReload} className="gap-2">
            <RefreshCcw className="h-4 w-4" />
            {isPageBoundary ? "Recarregar página" : "Recarregar sistema"}
          </Button>
          {isPageBoundary && (
            <Button variant="outline" onClick={this.handleGoHome} className="gap-2">
              <Home className="h-4 w-4" />
              Ir para o Dashboard
            </Button>
          )}
        </div>
      </div>
    );

    if (isPageBoundary) {
      return (
        <div className="flex min-h-[60vh] items-center justify-center px-4">
          {errorCard}
        </div>
      );
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        {errorCard}
      </div>
    );
  }
}
